import Match from "./Match";
import {t} from "../../i18n";

import './AudienceKey.css'
import {useLayoutEffect, useState} from "react";
import { AutoKey, TeleopKey } from "../components/decode/Key";
import {
    ArtifactType, DecodeTeleopBaseLocation,
    DisplayCommand,
    DisplayCommandType,
    ShowKey, ShowMatch, TournamentLevel
} from "../../javaTypes";
import {DisplayConfig} from "../Config";
import {useSlides} from "../util";

// From https://iconmonstr.com/arrow-right-thin-svg/
export const rightArrow = <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={"rightArrow"}>
    <path stroke={"white"} d="M21.883 12l-7.527 6.235.644.765 9-7.521-9-7.479-.645.764 7.529 6.236h-21.884v1h21.883z"/>
</svg>

interface Layout {
    calloutY?: string;
    calloutX?: string;
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
}

export function getPages(config: DisplayConfig) {
    const overlay = config.overlay || config.fieldStyleTimer;
    return overlay ? ['all'] as const :  ['auto', 'teleop'] as const
}

export function StatelessKey({isSingleTeam, config, nowCallback, phase} : {
    isSingleTeam: boolean,
    config: DisplayConfig,
    nowCallback: () => number,
    phase: 'auto' | 'teleop' | 'all'
}) {
    const overlay = config.overlay || config.fieldStyleTimer;
    const [layout, setLayout] = useState<Layout>({});

    const hflip = config.allianceOrientation === 'flipped';
    const vflip = config.scoringBarLocation === 'top';
    let popoutDirection : 'right' | 'top' | 'bottom' | 'left' = 'right';
    if(overlay) {
        popoutDirection = vflip ? 'bottom' : 'top';
    }
    let useRed = !hflip;
    if(overlay && phase === 'teleop') {
        useRed = !useRed;
    }
    const score = {
        adjust: 0,
        clazz: 'placeholder',
        autoClassifiedArtifacts: 0,
        autoOverflowArtifacts: 0,
        autoClassifierState: [ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE],
        robot1Auto: true,
        robot2Auto: false,
        teleopClassifiedArtifacts: 4,
        teleopOverflowArtifacts: 3,
        teleopDepotArtifacts: 0,
        teleopClassifierState: [ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE],
        robot1Teleop: DecodeTeleopBaseLocation.NONE,
        robot2Teleop: DecodeTeleopBaseLocation.NONE,
        minorFouls: 0,
        majorFouls: 0,
        autoLeavePoints: 0,
        autoArtifactPoints: 0,
        autoPatternPoints: 12,
        teleopArtifactPoints: 0,
        teleopDepotPoints: 0,
        teleopPatternPoints: 0,
        teleopBasePoints: 0,
        autoPoints: 0,
        teleopPoints: 0,
        foulPointsCommitted: 0,
        preFoulTotal: 25,
        violations: [],
        ownMajorFouls: 0,
        ownMinorFouls: 0,
        otherMajorFouls: 0,
        otherMinorFouls: 0,
        hrMajorFouls: 0,
        hrMinorFouls: 0,
        movementRP: false,
        goalRP: false,
        patternRP: false
    }
    const otherScore = {
        adjust: 0,
        clazz: 'placeholder',
        autoClassifiedArtifacts: 2,
        autoOverflowArtifacts: 1,
        autoClassifierState: [ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE],
        robot1Auto: false,
        robot2Auto: false,
        teleopClassifiedArtifacts: 0,
        teleopOverflowArtifacts: 0,
        teleopDepotArtifacts: 0,
        teleopClassifierState: [ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE, ArtifactType.NONE],
        robot1Teleop: DecodeTeleopBaseLocation.NONE,
        robot2Teleop: DecodeTeleopBaseLocation.NONE,
        minorFouls: 0,
        majorFouls: 0,
        autoLeavePoints: 0,
        autoArtifactPoints: 0,
        autoPatternPoints: 0,
        teleopArtifactPoints: 0,
        teleopDepotPoints: 0,
        teleopPatternPoints: 0,
        teleopBasePoints: 0,
        autoPoints: 7,
        teleopPoints: 0,
        foulPointsCommitted: 0,
        preFoulTotal: 7,
        violations: [],
        ownMajorFouls: 0,
        ownMinorFouls: 0,
        otherMajorFouls: 0,
        otherMinorFouls: 0,
        hrMajorFouls: 0,
        hrMinorFouls: 0,
        movementRP: false,
        goalRP: false,
        patternRP: false
    }
    const exampleCommand: DisplayCommand<ShowMatch> = {
        type: DisplayCommandType.SHOW_MATCH,
        ts: nowCallback(),
        index: 0,
        field: 0,
        params: {
            red: {
                teams: [
                    { number: '99999', name: 'tba', carriesCard: false, ranking: 0, leagueRanking: 0 },
                    { number: '88888', name: 'tba', carriesCard: false, ranking: 0, leagueRanking: 0 },
                ],
                wins: 0,
                losses: -1,
                ties: -1,
                seed: '',
                wonSeries: false
            },
            blue: {
                teams: [
                    { number: '66666', name: 'tba', carriesCard: false, ranking: 0, leagueRanking: 0 },
                    { number: '55555', name: 'tba', carriesCard: false, ranking: 0, leagueRanking: 0 },
                ],
                wins: 0,
                losses: -1,
                ties: -1,
                seed: '',
                wonSeries: false
            },
            matchName: t`Example Match`,
            number: 1,
            series: 0,
            displayNumber: 1,
            tournamentLevel: TournamentLevel.QUALIFICATION,
            field: 1,
            singleTeam: isSingleTeam
        },
        init: false
    };

    const scoreUpdate = {
        type: DisplayCommandType.SCORE_UPDATE,
        ts: 0,
        index: -1,
        field: 0,
        params: {
            redScores: hflip ? score : otherScore,
            blueScores: hflip ? otherScore : score,
            scores: score,
            hrReview: false,
            rand: 1,

            red: {
                teams: [],
                wins: 0,
                losses: -1,
                ties: -1,
                seed: '',
                wonSeries: false
            },
            blue: {
                teams: [],
                wins: 0,
                losses: -1,
                ties: -1,
                seed: '',
                wonSeries: false
            },
            matchName: t`Example Match`,
            number: 1,
            series: 0,
            displayNumber: 1,
            tournamentLevel: TournamentLevel.QUALIFICATION,
            finals: false,
            field: 1,
            singleTeam: isSingleTeam
        },
        init: false
    }

    const keyBody = overlay ? <div className={`audienceKey audienceKeyOverlay ${vflip ? 'vflip' : ''}`}>
        <div className={`autoKey ${useRed ? 'red' : 'blue'}`}>
            <h3>{t`AUTO Key`}</h3>
            <AutoKey color={useRed ? 'red' : 'blue'} />
        </div>
        <div className={`driverControlledCar ${useRed ? 'blue' : 'red'}`}>
            <h3>{t`TELEOP Key`}</h3>
            <TeleopKey color={useRed ? 'blue' : 'red'} />
        </div>
    </div> : <>
        <div className={"audienceKeyBackground"} />
        <div className={`audienceKey ${vflip ? 'vflip' : ''} ${overlay ? 'overlay' : ''} ${['top', 'bottom'].includes(popoutDirection) ? 'horizontal' : 'vertical'}
                ${popoutDirection} ${isSingleTeam ? 'singleTeam' : null}
                ${useRed ? 'red' : 'blue'}`}
             style={{'--offsetPosition': layout[popoutDirection],
             }}>
            {overlay ? <h3>{phase === 'auto' ? t`AUTO` : t`TELEOP`}</h3> : null}
            {phase == 'auto' ? <AutoKey color={isSingleTeam ? 'orange' : (useRed ? 'red' : 'blue')} /> : <TeleopKey color={isSingleTeam ? 'orange' : (useRed ? 'red' : 'blue')} />}
        </div>
    </>

    useLayoutEffect(() => {
        const keyPanel = document.querySelector(".keyPanel") as HTMLElement
        const displayContainer = document.querySelector(".displayContainer") as HTMLElement
        if(keyPanel) {
            // despite what typescript thinks, this actually does return a `number`
            const transform = getComputedStyle(keyPanel).getPropertyValue('--display-scale') as unknown as number
            const rect = keyPanel.getBoundingClientRect()
            const relativeRect = displayContainer.getBoundingClientRect()
            const yOffset = (rect.y - relativeRect.y) / transform
            const xOffset = (rect.x - relativeRect.x) / transform
            const newLayout: Layout = {
                calloutY: `${yOffset + keyPanel.offsetHeight / 2}px`,
                calloutX: `${xOffset + keyPanel.offsetWidth / 2}px`,
                top: `${(rect.top - relativeRect.top) / transform}px`,
                left: `${(rect.left - relativeRect.left) / transform}px`,
                bottom: `${(rect.bottom - relativeRect.top) / transform}px`,
                right: `${(rect.right - relativeRect.left) / transform}px`
            }
            if(JSON.stringify(newLayout) !== JSON.stringify(layout)) {
                setLayout(newLayout)
            }
        }

    }, [phase]);

    return <>
        <Match config={config} command={exampleCommand} nowCallback={nowCallback} keyPanel={phase} scoreUpdate={scoreUpdate}
               audienceKey={keyBody}
        />

    </>
}

export default function AudienceKey({command, config, nowCallback} : {
    command: DisplayCommand<ShowKey>,
    config: DisplayConfig,
    nowCallback: () => number
}) {
    const pages = getPages(config)

    const currentPage = useSlides(pages.length)

    return <StatelessKey isSingleTeam={command.params.singleTeam} config={config} nowCallback={nowCallback} phase={pages[currentPage]} />
}